import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Col } from "react-bootstrap"

import { COMPLIANCE, REVIEW, REQUIREMENTS, DANGER } from "const"
import { StoreType } from "client/store"
import {
    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmBottom,
    FormGroup,
    CheckboxWrapper,
    CheckButton,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

import ComplianceExportToPDF from "./compliance"
import ReviewComplianceExportToPDF from "./review"
import RequirementExportToPDF from "./requirement"

interface ExportToPDFProps {
    show: boolean
    defaultReport: typeof COMPLIANCE | typeof REVIEW | typeof REQUIREMENTS
    onClose: () => void
}

const ExportToPDF = ({
    show,
    defaultReport,
    onClose,
}: ExportToPDFProps) => {
    const complianceRevisionActiveList = useSelector((state: StoreType) => state.ccCompliance.revisionActiveList)
    const reviewComplianceRevisionActiveList = useSelector((state: StoreType) => state.ccReviewCompliance.revisionActiveList)
    const reviewReviewRevisionActiveList = useSelector((state: StoreType) => state.ccReviewReview.revisionActiveList)
    const requirementRevisionActiveList = useSelector((state: StoreType) => state.ccRequirement.revisionActiveList)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const langReport = useSelector((state: StoreType) => state.system.langReport)

    const [compliance, setCompliance] = useState(defaultReport === COMPLIANCE)
    const [review, setReview] = useState(defaultReport === REVIEW)
    const [requirements, setRequirements] = useState(defaultReport === REQUIREMENTS)

    const [complianceInProgress, setComplianceInProgress] = useState(false)
    const [reviewInProgress, setReviewInProgress] = useState(false)
    const [requirementsInProgress, setRequirementsInProgress] = useState(false)

    const printReports = () => {
        if (compliance) {
            ComplianceExportToPDF(
                complianceRevisionActiveList,
                project,
                moduleList,
                userList,
                userSettings,
                userData,
                langReport,
                setComplianceInProgress,
            )
        }
        if (review) {
            ReviewComplianceExportToPDF(
                reviewComplianceRevisionActiveList,
                reviewReviewRevisionActiveList,
                project,
                moduleList,
                userList,
                userSettings,
                userData,
                langReport,
                setReviewInProgress,
            )
        }
        if (requirements) {
            RequirementExportToPDF(
                requirementRevisionActiveList,
                project,
                moduleList,
                userList,
                userSettings,
                userData,
                langReport,
                setRequirementsInProgress,
            )
        }
    }

    const selectAll = (allSelected: boolean) => {
        if (allSelected) {
            setCompliance(false)
            setReview(false)
            setRequirements(false)
        } else {
            setCompliance(true)
            setReview(true)
            setRequirements(true)
        }
    }

    const allSelected = compliance && review && requirements
    const exportInProgress = complianceInProgress || reviewInProgress || requirementsInProgress

    return (
        <ConfirmWrapper
            show={show}
            onClose={onClose}
        >
            <Confirm>
                <ConfirmTitle>{lang.selectModulesForReport}</ConfirmTitle>

                <FormGroup>
                    <CheckboxWrapper>
                        <Col xs={12}>
                            <CheckButton
                                checked={compliance}
                                onChange={() => setCompliance(!compliance)}
                                title={lang.compliance}
                            />
                            <CheckButton
                                checked={review}
                                onChange={() => setReview(!review)}
                                title={lang.reviewCompliance}
                            />
                            <CheckButton
                                checked={requirements}
                                onChange={() => setRequirements(!requirements)}
                                title={lang.requirementsReview}
                            />
                        </Col>
                    </CheckboxWrapper>
                </FormGroup>

                <FormGroup>
                    <Feedback variant={DANGER}>{lang.reportNotificationCompliance}</Feedback>
                </FormGroup>

                <ConfirmBottom>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                    <Button
                        row
                        disabled={exportInProgress}
                        onClick={() => selectAll(allSelected)}
                    >
                        { allSelected ? lang.deselectAll : lang.selectAll }
                    </Button>

                    <Button
                        row
                        variant="success"
                        disabled={exportInProgress}
                        onClick={printReports}
                    >
                        {
                            exportInProgress
                                ? <SpinnerIcon />
                                : lang.export
                        }
                    </Button>
                </ConfirmBottom>
            </Confirm>
        </ConfirmWrapper>
    )
}
export default ExportToPDF
